import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { fetchUserInfo } from "api/queries/user";
import { updateUser, changePassword } from "api/mutators/user";
//import images
import progileBg from "../../../assets/images/profile-bg.jpg";
import avatar1 from "../../../assets/images/users/avatar-1.jpg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
/**
 * Form Validation Schema
 */

function timeout(delay: number) {
  return new Promise((res) => setTimeout(res, delay));
}

const schema = yup.object().shape({
  name: yup
    .string()
    .required("E' obbligatorio indicare il nome della campagna"),
  surname: yup
    .string()
    .required("E' obbligatorio indicare il nome della campagna"),
  telephone: yup.string().optional(),
  email: yup.string().email().required(),
  website: yup
    .string()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      "Enter correct url!"
    ),
  country: yup.string(),
  city: yup.string(),
  zipcode: yup.string().matches(/^[0-9]+$/, "Must be only digits"),
  description: yup.string(),
});

const schemaPassword = yup.object().shape({
  password: yup.string().required(),
  confirm: yup.string().required(),
  newPassword: yup.string().required(),
});

const defaultValues = {
  name: "",
  surname: "",
  telephone: "",
  email: "",
  website: "",
  country: "",
  city: "",
  zipcode: "",
  description: "",
};

const defaultValuesPassword = {
  password: "",
  confirm: "",
  newPassword: "",
};

const Settings = () => {
  const { t } = useTranslation("translation", { keyPrefix: "PROFILE" });
  const [activeTab, setActiveTab] = useState("1");

  const { isPending, isError, data, error } = useQuery({
    queryKey: fetchUserInfo.queryKey(),
    queryFn: () => fetchUserInfo.queryFn(),
  });

  // ** Hook
  const {
    reset,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const {
    reset: resetPassword,
    control: controlPassword,
    handleSubmit: handleSubmitPassword,
    getValues: getValuesPassword,
    formState: { errors: errorsPassword },
  } = useForm({
    defaultValues: defaultValuesPassword,
    mode: "onChange",
    resolver: yupResolver(schemaPassword),
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateUser,
    onError: async (error: any) => {
      toast.error("Error! User not Updated", { autoClose: 3000 });
      await timeout(3000);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["fetchUserInfo"] });
      toast.success("User Updated Successfully", { autoClose: 3000 });
      await timeout(3000);
    },
  });

  const mutationPassword = useMutation({
    mutationFn: changePassword,
    onError: async (error: any) => {
      toast.error("Error! Password not Updated", { autoClose: 3000 });
      await timeout(3000);
    },
    onSuccess: async () => {
      reset(defaultValues);
      queryClient.invalidateQueries({ queryKey: ["fetchUserInfo"] });
      toast.success("Password Updated Successfully", { autoClose: 3000 });
      await timeout(3000);
    },
  });

  function onSubmit() {
    mutation.mutate({ ...getValues() });
  }

  function onSubmitPassword() {
    mutationPassword.mutate({ ...getValuesPassword() });
  }

  useEffect(() => {
    if (!isPending) {
      reset({
        name: data?.name,
        surname: data?.surname,
        telephone: data?.telephone || "",
        email: data?.email || "",
        website: data?.website || "",
        country: data?.country || "",
        city: data?.city || "",
        zipcode: data?.zipcode || "",
        description: data?.description || "",
      });
    }
  }, [data, isPending, reset]);

  const tabChange = (tab: any) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  document.title = "Profile Settings | AICare  ";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="position-relative mx-n4 mt-n4">
            <div className="profile-wid-bg profile-setting-img">
              <img src={progileBg} className="profile-wid-img" alt="" />
            </div>
          </div>
          <Row>
            <Col xxl={3}>
              <Card className="mt-n5 card-bg-fill">
                <CardBody className="p-4">
                  <div className="text-center">
                    <div className="profile-user position-relative d-inline-block mx-auto  mb-4">
                      <img
                        src={avatar1}
                        className="rounded-circle avatar-xl img-thumbnail user-profile-image material-shadow"
                        alt="user-profile"
                      />
                      <div className="avatar-xs p-0 rounded-circle profile-photo-edit">
                        <Input
                          id="profile-img-file-input"
                          type="file"
                          className="profile-img-file-input"
                        />
                        <Label
                          htmlFor="profile-img-file-input"
                          className="profile-photo-edit avatar-xs"
                        >
                          <span className="avatar-title rounded-circle bg-light text-body material-shadow">
                            <i className="ri-camera-fill"></i>
                          </span>
                        </Label>
                      </div>
                    </div>
                    <h5 className="fs-16 mb-1">{`${getValues().name} ${
                      getValues().surname
                    }`}</h5>
                    <p className="text-muted mb-0"></p>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={9}>
              <Card className="mt-xxl-n5 card-bg-fill">
                <CardHeader>
                  <Nav
                    className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => {
                          tabChange("1");
                        }}
                      >
                        <i className="fas fa-home"></i>
                        {t("LAYOUT:FIRST_TAB")}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        to="#"
                        className={classnames({ active: activeTab === "2" })}
                        onClick={() => {
                          tabChange("2");
                        }}
                        type="button"
                      >
                        <i className="far fa-user"></i>
                        {t("LAYOUT:SECOND_TAB")}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody className="p-4">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="name" className="form-label">
                                {t("MODALS:NAME")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="name"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      id="name"
                                      placeholder="Enter your firstname"
                                      invalid={!!errors.name}
                                    />
                                  )}
                                />
                              </div>
                              {errors.name && (
                                <FormFeedback>
                                  {errors.name.message}
                                </FormFeedback>
                              )}
                            </FormGroup>
                          </Col>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="surname" className="form-label">
                                {t("MODALS:SURNAME")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="surname"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      className="form-control"
                                      id="surname"
                                      placeholder="Enter your lastname"
                                      invalid={!!errors.surname}
                                    />
                                  )}
                                />
                                {errors.surname && (
                                  <FormFeedback>
                                    {errors.surname.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label
                                  htmlFor="telephone"
                                  className="form-label"
                                >
                                  {t("MODALS:TELEPHONE")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="telephone"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      className="form-control"
                                      id="telephone"
                                      placeholder="Enter your phone number"
                                      invalid={!!errors.telephone}
                                    />
                                  )}
                                />

                                {errors.telephone && (
                                  <FormFeedback>
                                    {errors.telephone.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>

                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="email" className="form-label">
                                {t("MODALS:EMAIL")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="email"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="email"
                                      className="form-control"
                                      id="email"
                                      placeholder="Enter your email"
                                      invalid={!!errors.email}
                                      disabled
                                    />
                                  )}
                                />
                                {errors.email && (
                                  <FormFeedback>
                                    {errors.email.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="website" className="form-label">
                                {t("MODALS:WEBSITE")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="website"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      className="form-control"
                                      id="website"
                                      placeholder="www.example.com"
                                      invalid={!!errors.website}
                                    />
                                  )}
                                />
                                {errors.website && (
                                  <FormFeedback>
                                    {errors.website.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="city" className="form-label">
                                {t("MODALS:CITY")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="city"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      id="city"
                                      placeholder="City"
                                      invalid={!!errors.city}
                                    />
                                  )}
                                />
                                {errors.city && (
                                  <FormFeedback>
                                    {errors.city.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>

                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="country" className="form-label">
                                {t("MODALS:COUNTRY")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="country"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="text"
                                      id="country"
                                      placeholder="Country"
                                      invalid={!!errors.country}
                                    />
                                  )}
                                />
                                {errors.city && (
                                  <FormFeedback>
                                    {errors?.country?.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg={6}>
                            <FormGroup>
                              <div className="mb-3">
                                <Label htmlFor="zipcode" className="form-label">
                                {t("MODALS:ZIP_CODE")}
                                </Label>
                                <Controller
                                  control={control}
                                  name="zipcode"
                                  render={({ field }) => (
                                    <Input
                                      type="text"
                                      {...field}
                                      className="form-control"
                                      id="zipcode"
                                      placeholder="Enter zipcode"
                                      invalid={!!errors.zipcode}
                                    />
                                  )}
                                />
                                {errors.zipcode && (
                                  <FormFeedback>
                                    {errors.zipcode.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg={12}>
                            <div className="mb-3 pb-2">
                              <Label
                                htmlFor="description"
                                className="form-label"
                              >
                                {t("MODALS:DESCRIPTION")}
                              </Label>
                              <Controller
                                control={control}
                                name="description"
                                render={({ field }) => (
                                  <textarea
                                    {...field}
                                    className="form-control"
                                    id="description"
                                    rows={3}
                                  ></textarea>
                                )}
                              />
                            </div>
                          </Col>

                          <Col lg={12}>
                            <div className="hstack gap-2 justify-content-end">
                              <button type="submit" className="btn btn-primary">
                              {t("MODALS:SAVE")}
                              </button>
                              <button
                                type="button"
                                className="btn btn-soft-success"
                              >
                              {t("MODALS:CLOSE")}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>

                    <TabPane tabId="2">
                      <Form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                        <Row className="g-2">
                          <Col lg={4}>
                            <FormGroup>
                              <div>
                                <Label
                                  htmlFor="password"
                                  className="form-label"
                                >
                                  {t("MODALS:OLD_PASS")}*
                                </Label>
                                <Controller
                                  control={controlPassword}
                                  name="password"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="password"
                                      id="password"
                                      placeholder="Enter current password"
                                      invalid={!!errorsPassword.password}
                                    />
                                  )}
                                />
                                {errorsPassword.password && (
                                  <FormFeedback>
                                    {errorsPassword.password.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>

                          <Col lg={4}>
                            <FormGroup>
                              <div>
                                <Label
                                  htmlFor="newPassword"
                                  className="form-label"
                                >
                                  {t("MODALS:NEW_PASS")}*
                                </Label>
                                <Controller
                                  control={controlPassword}
                                  name="newPassword"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="password"
                                      id="newPassword"
                                      placeholder="Enter new password"
                                      invalid={!!errorsPassword.newPassword}
                                    />
                                  )}
                                />
                                {errorsPassword.newPassword && (
                                  <FormFeedback>
                                    {errorsPassword.newPassword.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>

                          <Col lg={4}>
                            <FormGroup>
                              <div>
                                <Label htmlFor="confirm" className="form-label">
                                {t("MODALS:CONFIRM_PASS")}*
                                </Label>
                                <Controller
                                  control={controlPassword}
                                  name="confirm"
                                  render={({ field }) => (
                                    <Input
                                      {...field}
                                      type="password"
                                      id="confirm"
                                      placeholder="Confirm password"
                                      invalid={!!errorsPassword.confirm}
                                    />
                                  )}
                                />
                                {errorsPassword.confirm && (
                                  <FormFeedback>
                                    {errorsPassword.confirm.message}
                                  </FormFeedback>
                                )}
                              </div>
                            </FormGroup>
                          </Col>

                          <Col lg={12}>
                            <div className="text-end">
                              <button type="submit" className="btn btn-soft-danger">
                              {t("MODALS:SAVE")}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default Settings;
